import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Calendar, Mail, MessageSquare, Phone, MapPin } from 'lucide-react';
import { Helmet } from 'react-helmet';
import { MrAvatar } from '../components/MrAvatar';
/*
  SEO SPECIFICATION:
  Title: Contact MindStream.ing — Talk to MR Directly | MindStream.ing
  Meta Desc: Say hey to MISTR. Fill out the form and get a response in under 2 minutes. No gatekeeping. No waiting. Strategy call included with every inquiry.
  Primary KW: contact MindStream.ing
*/
export function Contact() {
  const [formData, setFormData] = useState({
    name: '',
    businessName: '',
    email: '',
    phone: '',
    fix: 'Too many tasks falling through cracks'
  });
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    // Form submission logic would go here
    console.log('Form submitted:', formData);
  };
  return <div className="min-h-screen flex flex-col" data-id="element-1442" data-mp="abcyj6ln1g9x">
      <Helmet data-id="element-1443" data-mp="aalemt1el6zh">
        <title data-id="element-1444" data-mp="aauwftcao7u3">Contact MindStream.ing | Say Hey to MISTR</title>
        <meta name="description" content="Say hey to MISTR. Fill out the form and get a response in under 2 minutes. No gatekeeping. No waiting. Strategy call included with every inquiry." data-id="element-1445" data-mp="15edsxs5j5mox" />
      </Helmet>

      <main className="pt-20 flex-grow" data-id="element-1446" data-mp="1e8imijc1tc85">
        {/* 1. Hero Section */}
        <section className="relative py-24 px-6 bg-hero-gradient overflow-hidden" data-id="element-1447" data-mp="1j5iks82npaop">
          {/* Ambient Glow Orbs */}
          <div className="absolute top-0 right-0 w-[600px] h-[600px] bg-purple/20 rounded-full blur-[120px] opacity-30 pointer-events-none" data-id="element-1448" data-mp="midv40pvfoxf" />
          <div className="absolute bottom-0 left-0 w-[500px] h-[500px] bg-teal/20 rounded-full blur-[100px] opacity-20 pointer-events-none" data-id="element-1449" data-mp="vhit95xitg92" />

          {/* Grid Pattern Overlay */}
          <div className="absolute inset-0 opacity-[0.03] pointer-events-none" style={{
          backgroundImage: `radial-gradient(circle, white 1px, transparent 1px)`,
          backgroundSize: '40px 40px'
        }} data-id="element-1450" data-mp="26wejlp5j565g" />

          <div className="inner-container relative z-10 flex flex-col items-center justify-center py-8" data-id="element-1451" data-mp="1m5iplw7ntha8">
            <motion.div initial={{
            opacity: 0,
            scale: 0.8
          }} animate={{
            opacity: 1,
            scale: 1
          }} transition={{
            duration: 0.5
          }} className="mb-6" data-id="element-1452" data-mp="1h80sd3308qi2">
              <MrAvatar size={100} expression="winking" animate={true} data-id="element-1453" data-mp="yi12idzcb7dl" />
            </motion.div>
            <motion.h1 initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.5,
            delay: 0.2
          }} className="font-poppins font-extrabold text-5xl md:text-6xl leading-[1.1] text-center" data-id="element-1454" data-mp="1d6f82wo559xu">
              Say Hey to <span className="text-gradient" data-id="element-1455" data-mp="2rdv3uubsi939">MISTR.</span>
            </motion.h1>
          </div>
        </section>

        {/* 2. Content Section */}
        <section className="py-20 px-6 relative bg-white" data-id="element-1456" data-mp="2hgh09vaytesd">
          <div className="inner-container max-w-6xl mx-auto" data-id="element-1457" data-mp="2hi1pd5eig5iu">
            {/* Las Vegas Image - Full Width */}
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.5
          }} className="rounded-2xl overflow-hidden border border-gray-200 mb-12 shadow-lg" data-id="element-1458" data-mp="2mdh3lygfk3fs">
              <img src="https://adroimarketing.com/wp-content/uploads/2021/09/LasVegasMarketingCompany-1024x683.jpg" alt="Las Vegas Strip at night" className="w-full object-contain" data-id="element-1459" data-mp="3548b0uguxjrj" />
            </motion.div>

            <div className="grid lg:grid-cols-2 gap-12" data-id="element-1460" data-mp="29z2h73t7rgl4">
              {/* Left Column - Form */}
              <motion.div initial={{
              opacity: 0,
              x: -20
            }} animate={{
              opacity: 1,
              x: 0
            }} transition={{
              duration: 0.5
            }} className="bg-dark-bg backdrop-blur-md border border-white/10 rounded-2xl p-8 shadow-xl" data-id="element-1461" data-mp="2569d8i5kkca3">
                <form onSubmit={handleSubmit} className="space-y-6" data-id="element-1462" data-mp="2nvpgvecjwkuq">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-1463" data-mp="2ixe7f4q64cd1">
                    <div data-id="element-1464" data-mp="2exna0i64yxzj">
                      <label className="block text-sm font-medium text-steel mb-2" data-id="element-1465" data-mp="2ewc6mjglrs8y">
                        Name
                      </label>
                      <input type="text" required className="w-full bg-dark-bg/50 border border-white/10 rounded-lg px-4 py-3 text-white focus:outline-none focus:border-purple focus:bg-white/5 transition-colors" value={formData.name} onChange={e => setFormData({
                      ...formData,
                      name: e.target.value
                    })} data-id="element-1466" data-mp="17h7hyyzns2vv" />
                    </div>
                    <div data-id="element-1467" data-mp="6ph04a3ksdi1">
                      <label className="block text-sm font-medium text-steel mb-2" data-id="element-1468" data-mp="6opf1t7lty60">
                        Business Name
                      </label>
                      <input type="text" required className="w-full bg-dark-bg/50 border border-white/10 rounded-lg px-4 py-3 text-white focus:outline-none focus:border-purple focus:bg-white/5 transition-colors" value={formData.businessName} onChange={e => setFormData({
                      ...formData,
                      businessName: e.target.value
                    })} data-id="element-1469" data-mp="aozjh1fr0gir" />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-1470" data-mp="3otrn27ex7oz5">
                    <div data-id="element-1471" data-mp="7lnwcb9p280u">
                      <label className="block text-sm font-medium text-steel mb-2" data-id="element-1472" data-mp="7l4gd03lttpg">
                        Email
                      </label>
                      <input type="email" required className="w-full bg-dark-bg/50 border border-white/10 rounded-lg px-4 py-3 text-white focus:outline-none focus:border-purple focus:bg-white/5 transition-colors" value={formData.email} onChange={e => setFormData({
                      ...formData,
                      email: e.target.value
                    })} data-id="element-1473" data-mp="2kqgw6g2js31" />
                    </div>
                    <div data-id="element-1474" data-mp="19vurijqc03fh">
                      <label className="block text-sm font-medium text-steel mb-2" data-id="element-1475" data-mp="v1g3u5vto5uh">
                        Phone
                      </label>
                      <input type="tel" className="w-full bg-dark-bg/50 border border-white/10 rounded-lg px-4 py-3 text-white focus:outline-none focus:border-purple focus:bg-white/5 transition-colors" value={formData.phone} onChange={e => setFormData({
                      ...formData,
                      phone: e.target.value
                    })} data-id="element-1476" data-mp="zygaoj0hxcmn" />
                    </div>
                  </div>

                  <div data-id="element-1477" data-mp="7au8nf6x2g1w">
                    <label className="block text-sm font-medium text-steel mb-2" data-id="element-1478" data-mp="7d6n3idgu8a7">
                      What are you trying to fix?
                    </label>
                    <select className="w-full bg-dark-bg/50 border border-white/10 rounded-lg px-4 py-3 text-white focus:outline-none focus:border-purple focus:bg-white/5 transition-colors appearance-none" value={formData.fix} onChange={e => setFormData({
                    ...formData,
                    fix: e.target.value
                  })} data-id="element-1479" data-mp="it38cpw7dtp9">
                      <option data-id="element-1480" data-mp="3r83dfqgmrrhu">Too many tasks falling through cracks</option>
                      <option data-id="element-1481" data-mp="3m9a1w3kwmakb">Can't scale without hiring</option>
                      <option data-id="element-1482" data-mp="3h9e4woamjje9">Marketing is inconsistent</option>
                      <option data-id="element-1483" data-mp="3h8vxqwmfbkrk">Need a CRM that actually works</option>
                      <option data-id="element-1484" data-mp="3d7kauyuxpdn2">Everything — I need the full ecosystem</option>
                      <option data-id="element-1485" data-mp="1sq1js73tv36w">Other</option>
                    </select>
                  </div>

                  <button type="submit" className="btn-primary w-full py-4 text-lg mt-4 flex items-center justify-center gap-2" data-id="element-1486" data-mp="1wr3d67b2cl0y">
                    <MessageSquare size={20} data-id="element-1487" data-mp="26pj2c4qj5k7t" />
                    Talk to MR →
                  </button>
                </form>
              </motion.div>

              {/* Right Column - Contact Info */}
              <motion.div initial={{
              opacity: 0,
              x: 20
            }} animate={{
              opacity: 1,
              x: 0
            }} transition={{
              duration: 0.5,
              delay: 0.2
            }} className="flex flex-col gap-4" data-id="element-1488" data-mp="1e0ly35j5lny2">
                {/* Phone */}
                <div className="bg-dark-bg border border-white/10 rounded-xl p-6 flex items-center gap-5 hover:border-white/20 transition-colors" data-id="element-1489" data-mp="v9cchxw3z8q4">
                  <div className="bg-white/10 p-4 rounded-full text-white shrink-0" data-id="element-1490" data-mp="ozyg3h1fnv21">
                    <Phone size={24} data-id="element-1491" data-mp="tyrwbh83u05w" />
                  </div>
                  <div data-id="element-1492" data-mp="ywtgk5h9o5iy">
                    <p className="text-sm text-steel mb-1" data-id="element-1493" data-mp="ywjng90xdi91">Local Phone</p>
                    <a href="tel:17022134801" className="font-poppins font-semibold text-lg text-white hover:text-teal transition-colors" data-id="element-1494" data-mp="13ttmmge498a2">
                      1 (702) 213-4801
                    </a>
                  </div>
                </div>

                {/* Email */}
                <div className="bg-dark-bg border border-white/10 rounded-xl p-6 flex items-center gap-5 hover:border-white/20 transition-colors" data-id="element-1495" data-mp="g37g4tjrfw26">
                  <div className="bg-white/10 p-4 rounded-full text-white shrink-0" data-id="element-1496" data-mp="3jmsj1zfjlme6">
                    <Mail size={24} data-id="element-1497" data-mp="3okk9ep0jh0g9" />
                  </div>
                  <div data-id="element-1498" data-mp="3oizf90i4zjix">
                    <p className="text-sm text-steel mb-1" data-id="element-1499" data-mp="25ppksrm4dpya">Email</p>
                    <a href="mailto:hello@mindstream.ing" className="font-poppins font-semibold text-lg text-white hover:text-teal transition-colors" data-id="element-1500" data-mp="25nv9m50nhc7e">
                      hello@mindstream.ing
                    </a>
                  </div>
                </div>

                {/* Schedule a Meeting */}
                <div className="bg-dark-bg border border-white/10 rounded-xl p-6 flex items-center gap-5 hover:border-white/20 transition-colors" data-id="element-1501" data-mp="1mrwtynort6h9">
                  <div className="bg-white/10 p-4 rounded-full text-white shrink-0" data-id="element-1502" data-mp="1dyt7jpw5djah">
                    <Calendar size={24} data-id="element-1503" data-mp="1dwp1p2eoua7k" />
                  </div>
                  <div data-id="element-1504" data-mp="191006tj3yj8m">
                    <p className="text-sm text-steel mb-1" data-id="element-1505" data-mp="18xlyzom6kg2r">
                      Schedule a Meeting
                    </p>
                    <button className="font-poppins font-semibold text-lg text-white hover:text-teal transition-colors text-left" data-id="element-1506" data-mp="z1iw2gji8der">
                      Click Here to Book A Meeting
                    </button>
                  </div>
                </div>

                {/* Address */}
                <div className="bg-dark-bg border border-white/10 rounded-xl p-6 flex items-center gap-5 hover:border-white/20 transition-colors" data-id="element-1507" data-mp="uanbu5ucw7tu">
                  <div className="bg-white/10 p-4 rounded-full text-white shrink-0" data-id="element-1508" data-mp="kfvacqxnl0ts">
                    <MapPin size={24} data-id="element-1509" data-mp="1844s9hrqx4fn" />
                  </div>
                  <div data-id="element-1510" data-mp="186r25o23zw11">
                    <p className="text-sm text-steel mb-1" data-id="element-1511" data-mp="1d40ylnnx17yd">Address</p>
                    <p className="font-poppins font-medium text-white leading-relaxed" data-id="element-1512" data-mp="1d5c2dnhd2fph">
                      4730 S. Fort Apache Rd., Suite 300
                      <br data-id="element-1513" data-mp="13cmroutmx5v7" />
                      Las Vegas, NV 89147
                    </p>
                  </div>
                </div>
              </motion.div>
            </div>
          </div>
        </section>
      </main>
    </div>;
}